<template>
  <div class="flex flex-col">
    <div
      class="px-4 py-2.5 bg-white flex items-center gap-3"
      style="border-bottom: 1px solid #E8E9E9;"
    >
      <h2 class="text-lg font-semibold" style="color: #353537;">Summary</h2>
      <span
        class="text-xs px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide"
        style="background-color: #EEF2FF; color: #4338CA; border: 1px solid #A5B4FC;"
        :title="betaCaveat"
      >Beta &#9432;</span>
      <span class="text-xs" style="color: #626264;">
        Suspect review completion estimate &middot; currently-attributed MSSP panel
      </span>
      <span
        class="text-xs px-1.5 py-0.5 rounded"
        style="background-color: #FFFBEB; color: #B45309; border: 1px solid #FCD34D;"
        :title="scopeCaveat"
      >
        scoped differently to Operations V2 &#9432;
      </span>
    </div>

    <!-- Filters -->
    <div class="bg-white border-b border-gray-200 px-4 py-2">
      <div class="flex items-end flex-wrap gap-3">
        <div class="flex items-center gap-1.5">
          <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Practice</label>
          <div style="width: 220px;">
            <SimpleMultiSelect
              v-model="selectedPracticeIds"
              :options="practiceOptions"
              :sensitive-labels="true"
              :searchable="true"
              placeholder="All"
              @change="onPracticeChange"
            />
          </div>
        </div>

        <div class="flex items-center gap-1.5">
          <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Year</label>
          <div class="flex rounded-md overflow-hidden" style="border: 1px solid #E8E9E9;">
            <button
              v-for="y in yearOptions"
              :key="y"
              class="px-3 py-1 text-xs"
              :style="year === y
                ? 'background:#FEF3F0;color:#F76734;font-weight:600;'
                : 'background:#fff;color:#353537;'"
              @click="setYear(y)"
            >{{ y }}</button>
          </div>
        </div>

        <button
          v-if="selectedPracticeIds.length"
          @click="clearPractices"
          class="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 transition-colors whitespace-nowrap"
        >
          <FontAwesomeIcon :icon="['fas', 'times']" class="mr-1" />
          Clear
        </button>
      </div>

      <div v-if="selectedPracticeNames.length" class="mt-2 flex flex-wrap gap-2">
        <span
          v-for="p in selectedPracticeNames"
          :key="p.id"
          class="inline-flex items-center px-2 py-1 rounded-md text-xs bg-primary-100 text-primary-700"
        >
          <span class="sensitive">{{ p.label }}</span>
          <button
            @click="removePractice(p.id)"
            class="ml-1 hover:text-primary-900"
          >
            <FontAwesomeIcon :icon="['fas', 'times']" class="text-xs" />
          </button>
        </span>
      </div>
    </div>

    <LoadingIndicator v-if="loading" message="Calculating estimate..." />

    <div v-else-if="error" class="p-6">
      <div class="rounded p-4 text-sm" style="background-color: #FEF2F2; color: #991B1B;">
        {{ error }}
      </div>
    </div>

    <div v-else-if="data" class="flex flex-col">
      <!-- Program level. % of panel spans both rows on the left; to its right,
           two rows — opportunities then codes — each reading actual YTD,
           actual last 30d, projected / year.
           A 3-column grid (not flex) so YTD / 30d / projected line up
           vertically between the two rows. -->
      <!-- One scenario at a time, four measures each. Replaces the two-row
           grid that had grown a column per variant (YTD, projected, and the
           2LR-throttle projection all competing for the same row). The tabs
           make the BASIS explicit, which the old layout could only imply
           through label text. -->
      <div class="px-4 pt-3 pb-4" style="border-bottom: 1px solid #E8E9E9;">
        <div class="flex items-center gap-1 mb-3">
          <!-- `opt`, not `sc`: `sc` is the computed for the ACTIVE scenario and
               shadowing it here makes the template read as if the button showed
               the current basis rather than its own. -->
          <button
            v-for="(opt, key) in data.scenarios"
            :key="key"
            class="text-xs px-2 py-1 rounded"
            :style="key === scenario
              ? 'background-color: #21426C; color: #FFFFFF;'
              : 'background-color: #F3F4F6; color: #626264;'"
            @click="scenario = key"
          >{{ opt.label }}</button>
          <!-- The derivation travels with the scenario that needs it, rather
               than sitting on the page permanently. -->
          <span
            v-if="scenario === 'unthrottled'"
            class="text-xs ml-2 cursor-help"
            style="color: #B45309; border-bottom: 1px dotted #FCD34D;"
            :title="unthrottledBasis"
          >scenario, not an actual &#9432;</span>
        </div>

        <div class="grid gap-x-10" style="grid-template-columns: repeat(5, minmax(0, 1fr));">
          <div>
            <div class="text-2xl font-semibold" style="color: #353537;">
              {{ pct(sc.pct_panel) }}
            </div>
            <div class="text-xs mt-1" style="color: #626264;">
              {{ scenario === 'ytd' ? 'of panel reviewed' : 'of panel / year' }}
            </div>
          </div>
          <div>
            <div class="text-2xl font-semibold" style="color: #353537;">
              {{ num(sc.opportunities) }}
            </div>
            <div class="text-xs mt-1" style="color: #626264;">
              opportunities closed{{ scenario === 'ytd' ? ' YTD' : ' / year' }}
            </div>
          </div>
          <div>
            <div class="text-2xl font-semibold" style="color: #353537;">
              {{ num(sc.codes) }}
            </div>
            <div class="text-xs mt-1" style="color: #626264;">
              codes confirmed{{ scenario === 'ytd' ? ' YTD' : ' / year' }}
            </div>
          </div>
          <div>
            <div class="text-2xl font-semibold" style="color: #353537;">
              {{ raf(sc.raf) }}
            </div>
            <div class="text-xs mt-1" style="color: #626264;">
              RAF confirmed{{ scenario === 'ytd' ? ' YTD' : ' / year' }}
            </div>
          </div>
          <!-- A rate, so it does not change with the scenario. The label says
               so, otherwise a figure that holds still while its neighbours move
               reads as a bug. -->
          <div>
            <div class="text-2xl font-semibold" style="color: #353537;">
              {{ pct(sc.billed_pct) }}
            </div>
            <div class="text-xs mt-1" style="color: #626264;">
              <span
                v-if="sc.billed_denom"
                class="cursor-help"
                style="border-bottom: 1px dotted #A5A5A6;"
                :title="`${num(sc.billed_after)} of ${num(sc.billed_denom)} `
                  + 'confirmed codes whose 2-month window has fully elapsed. '
                  + 'A measured rate — the same on every scenario.'"
              >codes billed within 2mo</span>
              <span v-else>codes billed within 2mo</span>
            </div>
          </div>
        </div>

        <!-- Actuals the projections are built from, stated once so the basis is
             never a mystery. -->
        <div v-if="scenario !== 'ytd'" class="text-xs mt-3" style="color: #A5A5A6;">
          from the last {{ data.rate_days }} days:
          {{ num(t.closed_30d) }} opportunities closed,
          {{ num(t.confirmed_codes_30d) }} codes confirmed,
          {{ raf(t.raf_30d) }} RAF
          <template v-if="scenario === 'unthrottled'">
            &middot; scaled {{ data.scenarios.unthrottled.multiple }}&times; by
            relieving 2LR
          </template>
        </div>
      </div>

      <!-- Velocity -->
      <div v-if="data.trend && data.trend.length" style="border-bottom: 1px solid #E8E9E9;">
        <div class="px-4 pt-3 pb-1 flex items-baseline gap-3">
          <span class="text-sm font-semibold" style="color: #353537;">Velocity</span>
          <span class="text-xs" style="color: #A5A5A6;">
            weekly &middot; coder-actioned only, administrative closures excluded
            &middot; dashed line is a stock (RAF waiting in 2LR as at that week),
            not a flow
          </span>
        </div>
        <div class="px-2 pb-2">
          <Chart_V5 :chart_data="trendChart" :chart_config="trendConfig" override-height="h-[300px]" />
        </div>
      </div>

      <!-- Per practice -->
      <div class="px-4 pt-2 flex items-center gap-3" style="border-bottom: 1px solid #E8E9E9;">
        <PageTabs
          :tabs="viewTabs"
          :initial-tab="view"
          :no-border="true"
          @select="onViewTab"
        />
        <span v-if="view === 'rules'" class="text-xs" style="color: #A5A5A6;">
          {{ rulesData ? rulesData.total_gaps : 0 }} gaps &middot;
          {{ rulesData ? raf(rulesData.total_raf) : 0 }} RAF blocked &middot;
          in-flight work only
        </span>
        <span v-else class="text-xs" style="color: #A5A5A6;">
          {{ sorted.length }}
          {{ view === 'coder' ? 'coders' : 'practices' }} &middot; last
          {{ data.rate_days }} days
        </span>
        <div class="flex-1"></div>
        <span class="text-xs" style="color: #A5A5A6;">click a column to sort</span>
      </div>

      <!-- BOUNDED HEIGHT IS WHAT MAKES THE HEADER FREEZE. `sticky` resolves
           against the nearest scrolling ancestor, so with overflow-auto and no
           height the container never scrolled internally and the sticky header
           had nothing to stick to — it just scrolled away with the page.
           calc leaves room for the page chrome above the table. -->
      <div class="overflow-auto" style="max-height: calc(100vh - 340px);">
        <table class="w-full text-sm">
          <!-- STICKY IS PER-ROW, NOT PER-THEAD. Each <tr> needs its own top
               offset, so the band sits at 0 and the rows below are pushed down
               by the height of what precedes them.
               Borders stay COLLAPSED (the default): the body's row separators
               are border-top on <tr>, which border-separate does not render, so
               switching would trade a sticky seam for 3 missing row lines. The
               totals row therefore carries its own bottom border on the CELLS,
               where a collapsed table still paints it. -->
          <thead style="box-shadow: inset 0 -1px 0 #E8E9E9;">
            <!-- Group band. The four flow measures sit under one "Last 30 days"
                 header so the time basis is stated once; RAF in 2LR is
                 deliberately OUTSIDE it — that column is a stock (waiting right
                 now), not a 30-day flow. -->
            <!-- The band spans the practice view's four flow columns; in the
                 coder view every column is already a 30-day measure and the
                 caption says so, so the band would span nothing meaningful. -->
            <tr
              v-if="view === 'practice'"
              class="text-xs sticky z-20"
              style="top: 0; color: #A5A5A6; background-color: #FFFFFF;"
            >
              <th class="px-4 pt-2 pb-0"></th>
              <th class="px-4 pt-2 pb-0"></th>
              <th
                colspan="4"
                class="px-4 pt-2 pb-1 text-center font-medium"
                style="border-bottom: 1px solid #E8E9E9;"
              >
                Last 30 days
              </th>
              <!-- Billed after, RAF in 2LR, Not started, % panel/yr and Panel
                   all sit OUTSIDE the band: Billed after is YTD, the next two
                   are stocks, the last two are rates. -->
              <th class="px-4 pt-2 pb-0"></th>
              <th class="px-4 pt-2 pb-0"></th>
              <th class="px-4 pt-2 pb-0"></th>
              <th class="px-4 pt-2 pb-0"></th>
              <th class="px-4 pt-2 pb-0"></th>
            </tr>
            <tr
              class="text-xs sticky z-20"
              :style="{ top: view === 'practice' ? '25px' : '0px',
                        color: '#626264', backgroundColor: '#FFFFFF' }"
            >
              <!-- The header is BOTH a sort control and the column's
                   definition. The definition is a real popup rather than the
                   native title attribute: several of these measures are only
                   meaningful with their caveat (which window, which
                   denominator, stock vs flow), and a ~1s browser tooltip that
                   truncates is not where that belongs.
                   Hover the ⓘ, not the whole header, so moving across the row
                   to sort does not fire popups the reader did not ask for. -->
              <th
                v-for="col in columns"
                :key="col.key"
                class="px-4 pb-2 pt-1 font-medium select-none hover:bg-gray-50 relative"
                :class="[col.width, col.align === 'left' ? 'text-left' : 'text-right']"
                :style="{ color: sortKey === col.key ? '#353537' : '#626264' }"
              >
                <span class="cursor-pointer" @click="setSort(col.key)">
                  {{ col.label }}
                  <span v-if="col.subLabel" class="font-normal" style="color: #A5A5A6;">
                    {{ col.subLabel }}
                  </span>
                  <!-- Arrow only on the active column; a glyph on every header
                       reads as clutter and hides which one is live. -->
                  <span v-if="sortKey === col.key" style="color: #F76734;">
                    {{ sortDesc ? '&#9662;' : '&#9652;' }}
                  </span>
                </span>
                <span
                  v-if="col.hint"
                  class="ml-1 cursor-help font-normal"
                  style="color: #C4C4C6;"
                  @mouseenter="openHint(col, $event)"
                  @mouseleave="hintCol = null"
                >&#9432;</span>
                <!-- TELEPORTED to body. The table sits in an overflow-auto
                     wrapper, which CLIPS an absolutely-positioned child — the
                     popup would be cut off at the container edge, worst for the
                     right-hand columns that need it most. Teleport escapes the
                     scroll container; position comes from the icon's own
                     bounding rect, captured on hover.
                     Right-aligned columns anchor their RIGHT edge so the panel
                     opens inward instead of off-screen. -->
                <Teleport to="body">
                  <div
                    v-if="hintCol === col.key"
                    class="fixed z-50 p-2 rounded text-xs font-normal normal-case text-left"
                    :style="hintStyle"
                  >
                    <div class="font-semibold mb-1">
                      {{ col.label }}<template v-if="col.subLabel"> {{ col.subLabel }}</template>
                    </div>
                    {{ col.hint }}
                  </div>
                </Teleport>
              </th>
            </tr>
            <!-- COLUMN TOTALS, inside <thead> so it freezes with the header
                 rather than needing a computed top offset that would break the
                 moment a header row changed height.
                 Rates here are rebuilt from summed numerators/denominators, not
                 averaged across practices — see the `totals` computed. Columns
                 that do not legitimately sum (Coders, which would double-count a
                 coder working several practices) render BLANK, because a 0 would
                 assert something false. -->
            <tr
              v-if="totals"
              class="text-xs font-semibold sticky z-20"
              :style="{ top: view === 'practice' ? '54px' : '29px',
                        backgroundColor: '#F4F4F4', color: '#353537',
                        boxShadow: 'inset 0 -1px 0 #C4C4C6' }"
            >
              <td class="px-4 py-1.5" style="color: #626264;">
                {{ totals.practice_name }}
              </td>
              <td class="px-4 py-1.5"></td>
              <td class="px-4 py-1.5 text-right tabular-nums">{{ num(totals.recent) }}</td>
              <td class="px-4 py-1.5 text-right tabular-nums">{{ num(totals.closed_30d) }}</td>
              <td class="px-4 py-1.5 text-right tabular-nums">
                {{ num(totals.confirmed_codes_30d) }}
              </td>
              <td class="px-4 py-1.5 text-right tabular-nums">{{ raf(totals.raf_30d) }}</td>
              <td class="px-4 py-1.5 text-right tabular-nums">{{ pct(totals.billed_pct) }}</td>
              <td class="px-4 py-1.5 text-right tabular-nums">{{ raf(totals.wip_2lr_raf) }}</td>
              <td class="px-4 py-1.5 text-right tabular-nums">{{ num(totals.not_started) }}</td>
              <td class="px-4 py-1.5 text-right tabular-nums">{{ pct(totals.pct_per_year) }}</td>
              <td class="px-4 py-1.5 text-right tabular-nums">{{ num(totals.patients) }}</td>
            </tr>
          </thead>
          <!-- SUGGESTED RULES. Flat list, no expansion: every row is a single
               instruction ("give this practice a 2LR assignee"), so there is
               nothing to drill into. -->
          <tbody v-if="view === 'rules'">
            <tr v-if="rulesLoading">
              <td colspan="8" class="px-4 py-3 text-sm" style="color: #A5A5A6;">
                Finding blocked work&hellip;
              </td>
            </tr>
            <tr v-else-if="rulesError">
              <td colspan="8" class="px-4 py-3 text-sm" style="color: #991B1B;">
                {{ rulesError }}
              </td>
            </tr>
            <tr v-else-if="!sorted.length">
              <td colspan="8" class="px-4 py-3 text-sm" style="color: #78797A;">
                No blocked work. Every practice with in-progress charts has an
                active rule at the tier those charts sit in.
              </td>
            </tr>
            <tr
              v-for="r in sorted"
              :key="r.practice_entity_id + '-' + r.task_type_id"
              style="border-top: 1px solid #F3F4F6;"
            >
              <td class="sensitive px-4 py-1.5" style="color: #353537;">{{ r.practice_name }}</td>
              <td class="px-4 py-1.5">
                <span
                  class="text-xs px-1.5 py-0.5 rounded whitespace-nowrap"
                  style="background-color: #EBF4FF; color: #21426C; border: 1px solid #C1D6F0;"
                >{{ r.tier }}</span>
              </td>
              <td
                class="px-4 py-1.5 text-right tabular-nums font-semibold"
                style="color: #353537;"
              >{{ raf(r.raf) }}</td>
              <td class="px-4 py-1.5 text-right tabular-nums" style="color: #626264;">
                {{ num(r.charts) }}
              </td>
              <td class="px-4 py-1.5 text-right tabular-nums" style="color: #626264;">
                {{ num(r.codes) }}
              </td>
              <td class="px-4 py-1.5 text-right tabular-nums" style="color: #626264;">
                {{ num(r.avg_age_days) }}
              </td>
              <!-- Age is not the ranking, but a long tail is its own signal. -->
              <td
                class="px-4 py-1.5 text-right tabular-nums"
                :style="{ color: r.oldest_days >= 90 ? '#B45309' : '#626264',
                          fontWeight: r.oldest_days >= 90 ? 600 : 400 }"
              >{{ num(r.oldest_days) }}</td>
              <!-- A thin tier is flagged because "enable a rule" is then the
                   wrong advice — there is no one to route the work to. -->
              <td
                class="px-4 py-1.5 text-right tabular-nums"
                :style="{ color: r.coders_at_tier <= 2 ? '#B45309' : '#626264',
                          fontWeight: r.coders_at_tier <= 2 ? 600 : 400 }"
                :title="r.coders_at_tier <= 2
                  ? `Only ${r.coders_at_tier} coder(s) licensed for ${r.tier} in the `
                    + 'whole tenant — a rule cannot route work to capacity that '
                    + 'does not exist.'
                  : `${r.coders_at_tier} coders licensed for ${r.tier}`"
              >{{ r.coders_at_tier }}</td>
            </tr>
          </tbody>
          <tbody v-else-if="view === 'coder'">
            <template v-for="c in sorted" :key="c.coder_id">
              <tr
                class="cursor-pointer hover:bg-gray-50"
                style="border-top: 1px solid #F3F4F6;"
                @click="toggleCoder(c)"
              >
                <td class="px-4 py-1.5" style="color: #353537;">
                  <span class="inline-block w-3 mr-1" style="color: #A5A5A6;">
                    {{ expandedCoder === c.coder_id ? '&#9662;' : '&#9656;' }}
                  </span> <span class="sensitive">{{ c.coder }}</span>
                </td>
                <!-- 1LR-only screeners vs coders holding QA authority. Coloured
                     pill rather than a tier string: the distinction is a
                     category, and the list of tier names buried the fact that
                     37 of 44 coders are screeners. -->
                <td class="px-4 py-1.5">
                  <span
                    class="px-1.5 py-0.5 rounded text-xs font-medium"
                    :style="coderAuthority(c).style"
                    :title="coderAuthority(c).title"
                  >{{ coderAuthority(c).label }}</span>
                </td>
                <td class="px-4 py-1.5 text-right tabular-nums" style="color: #626264;">{{ num(c.tasks_30d) }}</td>
                <td class="px-4 py-1.5 text-right tabular-nums" style="color: #626264;">{{ num(c.codes_30d) }}</td>
                <td class="px-4 py-1.5 text-right tabular-nums" style="color: #626264;">{{ num(c.qa_performed_30d) }}</td>
                <!-- Reviewed count alongside the rate: without the denominator a
                     suppressed or extreme rate cannot be judged. -->
                <td
                  class="px-4 py-1.5 text-right tabular-nums"
                  style="color: #626264;"
                  :title="c.qa_reviewed_30d
                    ? `${num(c.qa_accepted_30d)} accepted, ${num(c.qa_rejected_30d)} rejected of ${num(c.qa_reviewed_30d)} reviewed`
                    : 'None of this coder\u2019s codes have been QA\u2019d yet'"
                >
                  <span v-if="c.qa_pct != null">{{ pct(c.qa_pct) }}</span>
                  <span v-else style="color: #C4C4C6;">&mdash;</span>
                  <span v-if="c.qa_reviewed_30d" class="text-xs" style="color: #A5A5A6;">
                    ({{ num(c.qa_reviewed_30d) }})
                  </span>
                </td>
                <td class="px-4 py-1.5 text-right tabular-nums" style="color: #626264;">{{ num(c.practices_30d) }}</td>
              </tr>
              <!-- Two panels, deliberately side by side: what the coder is
                   LICENSED for (rules), and what those rules would actually
                   SERVE them next (projection). Reading them together is the
                   point — a coder with 45 rules and an empty hopper is a
                   different problem from one with two rules and a full one.

                   Each panel is a WHITE CARD on a tinted inset, and the two
                   carry different accents because they are different KINDS of
                   thing: rules are static configuration (Navy — reference you
                   read), the hopper is live and actionable (Tangerine — the
                   brand action colour). Colour here encodes category, never
                   decoration. -->
              <tr v-if="expandedCoder === c.coder_id">
                <td colspan="7" class="p-0">
                  <div
                    class="px-8 py-4 flex items-stretch gap-4"
                    style="background-color: #F4F4F4; border-top: 1px solid #E8E9E9;
                           box-shadow: inset 0 1px 3px rgba(0,0,0,0.04);"
                  >

                    <!-- PANEL 1 — assignment rules. Already on the row payload,
                         so this renders instantly with no fetch. -->
                    <div
                      class="flex-1 min-w-0 rounded overflow-hidden"
                      style="background-color: #FFFFFF; border: 1px solid #E8E9E9;
                             border-top: 2px solid #21426C;"
                    >
                      <div
                        class="px-3 py-2 flex items-baseline justify-between"
                        style="background-color: #EBF4FF; border-bottom: 1px solid #E8E9E9;"
                      >
                        <span
                          class="text-xs font-semibold uppercase tracking-wide"
                          style="color: #21426C;"
                        >Assigned by rule</span>
                        <span v-if="c.rules.length" class="text-xs" style="color: #4C6E97;">
                          {{ c.rules.length }}
                          {{ c.rules.length === 1 ? 'practice' : 'practices' }}
                          &middot; worked {{ c.practices_30d }} in window
                        </span>
                      </div>
                      <div v-if="!c.rules.length" class="px-3 py-3 text-sm" style="color: #78797A;">
                        No practice-grain assignment rules. Work reaches this coder
                        by a global or TIN-scoped rule, or by direct assignment.
                      </div>
                      <div v-else class="overflow-y-auto" style="max-height: 220px;">
                        <table class="w-full text-sm">
                          <tbody>
                            <tr
                              v-for="(r, i) in c.rules"
                              :key="c.coder_id + '-' + r.practice_id"
                              :style="i % 2 ? 'background-color: #FAFAFA;' : ''"
                            >
                              <td class="sensitive px-3 py-1.5" style="color: #353537;">
                                {{ r.practice_name || ('Practice ' + r.practice_id) }}
                              </td>
                              <td class="px-3 py-1.5 w-32 text-right">
                                <span
                                  class="text-xs px-1.5 py-0.5 rounded whitespace-nowrap"
                                  style="background-color: #EBF4FF; color: #21426C;"
                                >{{ r.tiers }}</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <!-- PANEL 2 — projected hopper. Lazy: one request per coder
                         on expand (~1-2s), cached for the session. Doing all 43
                         on page load would be unusable. -->
                    <div
                      class="flex-1 min-w-0 rounded overflow-hidden"
                      style="background-color: #FFFFFF; border: 1px solid #E8E9E9;
                             border-top: 2px solid #F76734;"
                    >
                      <div
                        class="px-3 py-2 flex items-baseline justify-between"
                        style="background-color: #FFF7F4; border-bottom: 1px solid #E8E9E9;"
                      >
                        <span
                          class="text-xs font-semibold uppercase tracking-wide"
                          style="color: #9F4D30;"
                        >Next up</span>
                        <span
                          class="text-xs cursor-help"
                          style="color: #CC5F38; border-bottom: 1px dotted #CC5F38;"
                          title="Derived from this coder's assignment rules and the
                                 console's prioritization ladder: rework first, then
                                 Foundations (uncapped), then up to 10 charts at the
                                 current practice, then highest impact rank anywhere.
                                 A PROJECTION, NOT A RESERVATION — nothing is locked,
                                 so any of these can be claimed by another coder
                                 first. Only the first is dependable."
                        >projected &middot; not reserved</span>
                      </div>

                      <div v-if="hopperLoading[c.coder_id]" class="px-3 py-3 text-sm" style="color: #78797A;">
                        Calculating&hellip;
                      </div>
                      <div
                        v-else-if="hopperError[c.coder_id]"
                        class="px-3 py-3 text-sm"
                        style="color: #991B1B; background-color: #FEF2F2;"
                      >
                        {{ hopperError[c.coder_id] }}
                      </div>
                      <div
                        v-else-if="hopper[c.coder_id] && !hopper[c.coder_id].length"
                        class="px-3 py-3 text-sm" style="color: #78797A;"
                      >
                        Nothing eligible. Their rules match no open, unclaimed
                        charts right now.
                      </div>
                      <div v-else-if="hopper[c.coder_id]" class="overflow-y-auto" style="max-height: 220px;">
                        <table class="w-full text-sm">
                          <tbody>
                            <tr
                              v-for="(h, i) in hopper[c.coder_id]"
                              :key="c.coder_id + '-h-' + h.task_id"
                              :style="i % 2 ? 'background-color: #FAFAFA;' : ''"
                            >
                              <!-- Position is the sequence, so it reads as a rank
                                   chip rather than a table column of digits. The
                                   first is the only dependable one, so it alone
                                   gets the solid brand fill. -->
                              <td class="pl-3 pr-2 py-1.5 w-8">
                                <span
                                  class="inline-flex items-center justify-center text-xs font-semibold rounded-full"
                                  style="width: 18px; height: 18px;"
                                  :style="h.position === 1
                                    ? 'background-color: #F76734; color: #FFFFFF;'
                                    : 'background-color: #F4F4F4; color: #78797A;'"
                                >{{ h.position }}</span>
                              </td>
                              <td class="pr-3 py-1.5 min-w-0">
                                <div class="sensitive truncate" style="color: #353537;">
                                  {{ h.patient_name || h.patient_identifier || ('Task ' + h.task_id) }}
                                </div>
                                <div class="sensitive text-xs truncate" style="color: #8E8F90;">
                                  {{ h.practice_name || '\u2014' }}
                                </div>
                              </td>
                              <td class="pr-2 py-1.5 w-24">
                                <span
                                  class="text-xs px-1.5 py-0.5 rounded whitespace-nowrap"
                                  :style="hopperTierStyle(h.tier)"
                                  :title="h.reason"
                                >{{ h.tier_label }}</span>
                              </td>
                              <td class="pr-3 py-1.5 w-12 text-right">
                                <span
                                  class="tabular-nums text-xs font-semibold"
                                  style="color: #626264;"
                                  title="Impact Rank"
                                >{{ h.impact_rank == null ? '\u2014' : h.impact_rank.toFixed(1) }}</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                  </div>
                </td>
              </tr>
            </template>
            <tr v-if="!sorted.length">
              <td colspan="7" class="px-4 py-3 text-sm" style="color: #A5A5A6;">
                No coder activity in the window.
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <template v-for="p in sorted" :key="p.practice_entity_id">
              <tr
                class="cursor-pointer hover:bg-gray-50"
                style="border-top: 1px solid #F3F4F6;"
                @click="toggle(p)"
              >
                <!-- Flex row, not inline content: the chevron is a fixed-width
                     item and the name + badges sit in their own block, so a
                     badge that wraps to a second line aligns under the practice
                     name rather than back at the cell's left edge. -->
                <td class="px-4 py-1.5" style="color: #353537;">
                  <div class="flex items-start gap-1">
                    <span
                      class="flex-shrink-0 leading-5"
                      style="color: #A5A5A6; width: 0.75rem;"
                    >{{ expanded === p.practice_entity_id ? '&#9662;' : '&#9656;' }}</span>
                    <!-- flex-wrap keeps the name and its badges on one baseline
                         and wraps the overflow within this block. min-w-0 lets
                         the name shrink so `truncate` can actually bite —
                         without it a flex child refuses to go below its content
                         width and the ellipsis never appears. -->
                    <div class="flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0">
                      <span
                        class="sensitive leading-5 truncate"
                        style="max-width: 15rem;"
                        :title="p.practice_name"
                      >{{ p.practice_name }}</span>
                      <!-- All practice-level flags live here, cause before
                           symptom: tiers unrouted, then 2LR stale, then
                           closures lagging throughput. -->
                      <span
                        v-for="fl in practiceFlags(p)"
                        :key="fl.key"
                        class="px-1 rounded text-xs font-medium whitespace-nowrap"
                        :style="fl.style"
                        :title="fl.title"
                      >{{ fl.label }}</span>
                    </div>
                  </div>
                </td>
                <!-- current / all-time. Two different things, so they are
                     weighted differently rather than reading as a fraction:
                     CURRENT (an active assignment rule here) is the primary
                     figure in body colour; ALL-TIME (anyone who has completed
                     work here) is smaller and muted behind a light divider.
                     Current = 0 with all-time > 0 is the pathological case —
                     nobody is routed this practice yet people are working it —
                     so the zero turns red and carries its own tooltip. -->
                <td class="px-4 py-1.5 text-right tabular-nums" style="color: #626264;">
                  <span
                    :style="ruledCount(p) === 0 && p.coders.length
                      ? 'color: #DC2626; font-weight: 600;'
                      : 'color: #353537;'"
                    :title="ruledCount(p) === 0 && p.coders.length
                      ? `No coder holds an active assignment rule here, but `
                        + `${p.coders.length} have completed work for it.`
                      : `${ruledCount(p)} coder(s) currently assigned by rule`"
                  >{{ ruledCount(p) }}</span>
                  <span class="mx-0.5" style="color: #E8E9E9;">|</span>
                  <span
                    class="text-xs"
                    style="color: #A5A5A6;"
                    :title="`${p.coders.length} coder(s) have completed work `
                      + 'here at any point'"
                  >{{ p.coders.length }}</span>
                </td>
                <td class="px-4 py-1.5 text-right tabular-nums" style="color: #626264;">{{ num(p.recent) }}</td>
                <td class="px-4 py-1.5 text-right tabular-nums" style="color: #626264;">{{ num(p.closed_30d) }}</td>
                <td class="px-4 py-1.5 text-right tabular-nums" style="color: #626264;">
                  {{ num(p.confirmed_codes_30d) }}
                </td>
                <td class="px-4 py-1.5 text-right tabular-nums" style="color: #626264;">
                  {{ raf(p.raf_30d) }}
                </td>
                <!-- Fixed 2-month window, so practices are comparable, and
                     OUTSIDE the "Last 30 days" band. Codes younger than the
                     window are excluded from the denominator entirely. -->
                <!-- n/d is on the CELL's title, not rendered inline: two extra
                     numbers per row cost more width than they earn, and the
                     rate is the point. An empty <span> with a title would not
                     be hoverable, so the attribute belongs on the cell itself. -->
                <td
                  class="px-4 py-1.5 text-right tabular-nums"
                  style="color: #626264;"
                  :title="p.billed_denom
                    ? `${num(p.billed_after)} of ${num(p.billed_denom)} eligible confirmed codes`
                    : 'No confirmed codes old enough to measure'"
                >{{ pct(p.billed_pct) }}</td>
                <td
                  class="px-4 py-1.5 text-right tabular-nums"
                  style="color: #626264;"
                  :title="`${num(p.wip_2lr_codes)} codes across `
                    + `${num(p.wip_2lr_opps)} charts waiting in 2LR`"
                >{{ raf(p.wip_2lr_raf) }}</td>
                <!-- Untapped backlog. Shown with its share of the panel because
                     3,495 means something different at a 3,503-patient practice
                     than at a 30,000-patient one. -->
                <td
                  class="px-4 py-1.5 text-right tabular-nums"
                  style="color: #626264;"
                  :title="p.patients
                    ? `${pct(p.not_started / p.patients)} of this practice's panel`
                    : ''"
                >{{ num(p.not_started) }}</td>
                <td class="px-4 py-1.5 text-right tabular-nums" style="color: #626264;">{{ pct(p.pct_per_year) }}</td>
                <td class="px-4 py-1.5 text-right tabular-nums" style="color: #626264;">{{ num(p.patients) }}</td>
              </tr>
              <!-- CHILD PANEL. A SELF-CONTAINED CARD with its own table and its
                   own headers, deliberately NOT a set of cells spanning the
                   parent's columns. The previous version laid 3 content cells +
                   8 spacers across the parent grid, and every width had to
                   mirror practiceColumns positionally — nothing enforced that,
                   so it drifted and the per-coder Tasks figure sat under the
                   wrong header. A panel cannot drift: it owns its own grid. -->
              <tr v-if="expanded === p.practice_entity_id">
                <td colspan="11" class="p-0">
                  <div
                    class="px-8 py-4"
                    style="background-color: #F4F4F4; border-top: 1px solid #E8E9E9;
                           box-shadow: inset 0 1px 3px rgba(0,0,0,0.04);"
                  >
                    <div
                      class="rounded overflow-hidden"
                      style="background-color: #FFFFFF; border: 1px solid #E8E9E9;
                             border-top: 2px solid #21426C;"
                    >
                      <div
                        class="px-3 py-2 flex items-baseline justify-between"
                        style="background-color: #EBF4FF; border-bottom: 1px solid #E8E9E9;"
                      >
                        <span
                          class="text-xs font-semibold uppercase tracking-wide"
                          style="color: #21426C;"
                        >Coders at this practice</span>
                        <span class="text-xs" style="color: #4C6E97;">
                          {{ ruledCount(p) }} assigned by rule
                          &middot; {{ p.coders.length }} worked in window
                        </span>
                      </div>

                      <div v-if="!p.coders.length" class="px-3 py-3 text-sm" style="color: #78797A;">
                        No coders assigned, and no work completed here in the window.
                      </div>

                      <!-- TWO POPULATIONS, NOT ONE LIST. "Currently assigned by
                           rule" is who SHOULD get this practice's work (an active
                           OpportunityTaskTeam rule); "worked without a rule" is
                           who ACTUALLY did it with no such rule — 22pct of all
                           task completions. The group heading carries the whole
                           distinction, so the rows stay styled identically. -->
                      <table v-else class="w-full text-sm">
                        <thead>
                          <tr style="background-color: #FAFAFA; border-bottom: 1px solid #E8E9E9;">
                            <th
                              class="px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wide"
                              style="color: #626264;"
                            >Coder</th>
                            <th
                              class="px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wide w-40"
                              style="color: #626264;"
                            >Tier</th>
                            <th
                              class="px-3 py-1.5 text-right text-xs font-semibold uppercase tracking-wide w-24"
                              style="color: #626264;"
                            >Tasks 30d</th>
                          </tr>
                        </thead>
                        <tbody>
                          <template v-for="g in coderGroups(p)" :key="g.key">
                            <tr style="background-color: #FAFAFA;">
                              <td colspan="3" class="px-3 pt-2 pb-1">
                                <span
                                  class="text-xs font-semibold uppercase tracking-wide"
                                  style="color: #626264;"
                                >{{ g.label }}</span>
                                <span class="text-xs ml-2" style="color: #A5A5A6;">
                                  {{ g.rows.length }}
                                  {{ g.rows.length === 1 ? 'coder' : 'coders' }}
                                  &middot; {{ num(g.tasks) }} tasks
                                </span>
                              </td>
                            </tr>
                            <tr
                              v-for="c in g.rows"
                              :key="g.key + c.coder + (c.tier || '')"
                              style="border-top: 1px solid #F3F4F6;"
                            >
                              <td class="sensitive px-3 py-1.5" style="color: #353537;">{{ c.coder }}</td>
                              <td class="px-3 py-1.5 text-xs" style="color: #626264;">
                                {{ c.tier || '&mdash;' }}
                              </td>
                              <td
                                class="px-3 py-1.5 text-right tabular-nums"
                                :style="{ color: c.tasks_30d ? '#626264' : '#A5A5A6' }"
                              >{{ num(c.tasks_30d) }}</td>
                            </tr>
                          </template>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Coding Summary — suspect review completion estimate, per practice and overall.
 *
 * Backend: GET /api/coding/pace/ (CodingPaceService). Superuser-only.
 */
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import PageTabs from '@/components/widgetcomponents/Page_Tabs.vue';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';

export default {
  name: 'CodingSummary',
  components: { Chart_V5, FontAwesomeIcon, LoadingIndicator, PageTabs, SimpleMultiSelect },
  data() {
    const y = new Date().getFullYear();
    return {
      loading: true,
      error: null,
      data: null,
      year: y,
      yearOptions: [y - 2, y - 1, y],
      practiceOptions: [],
      selectedPracticeIds: [],
      // Which grain the table is showing.
      view: 'practice',
      // Coder whose child panels are expanded.
      expandedCoder: null,
      // Projected hopper, per coder. Lazy — one request on first expand, then
      // cached for the session. Eagerly loading all 43 would be ~1-2s each.
      // Keyed by coder_id in three parallel maps rather than one object per
      // coder so the template can test each state without optional chaining.
      hopper: {},
      hopperLoading: {},
      hopperError: {},
      // Suggested Rules. Lazy — its own endpoint, fetched on first switch to
      // that toggle and cached until the year changes.
      rulesData: null,
      rulesLoading: false,
      rulesError: null,
      // Column definitions drive BOTH the header row and the sort, so a new
      // column cannot end up sortable-but-unlabelled or vice versa.
      //   key    matches the field on a row (or 'practice_name' / 'coders',
      //          handled in sortValue)
      //   desc   the natural first direction — biggest-first for counts,
      //          A-Z for the name
      // The CODER view is deliberately a different, shorter set: panel size,
      // unstarted backlog and 2LR queue are properties of a PRACTICE, not of a
      // person, and carrying them over would invite reading a coder as
      // responsible for a queue they do not own.
      coderColumns: [
        { key: 'coder',            label: 'Coder',   align: 'left',  width: 'w-64', desc: false,
          hint: 'Coder who actioned the work. Rows merge three populations: '
            + 'tasks completed, codes authored, and QA performed — a coder can '
            + 'appear with any one of them. Click a row for their assigned '
            + 'practice rules.' },
        { key: 'authority',        label: 'Authority', align: 'left', width: 'w-40', desc: true,
          hint: 'What the coder is LICENSED for by their active assignment '
            + 'rules — not what they happened to work. 1LR-only screeners are '
            + 'separated from coders holding QA authority (2LR / 3LR / '
            + 'Confirm), because a rejection from a screener and one from a '
            + 'reviewer are not the same event.' },
        { key: 'tasks_30d',        label: 'Tasks',   subLabel: '30d', width: 'w-20', desc: true,
          hint: 'Review tasks COMPLETED in the last 30 days, any tier. '
            + 'Excludes administrative bulk closures.' },
        { key: 'codes_30d',        label: 'New codes', subLabel: '30d', width: 'w-24', desc: true,
          hint: 'Codes this coder AUTHORED in the last 30 days — new suggested '
            + 'codes, regardless of whether QA has ruled on them yet.' },
        { key: 'qa_performed_30d', label: 'QA done', subLabel: '30d', width: 'w-24', desc: true,
          hint: 'QA reviews this coder PERFORMED on other coders\u2019 codes in '
            + 'the last 30 days. Reviewer volume — distinct from QA pass, '
            + 'which judges their own work.' },
        { key: 'qa_pct',           label: 'QA pass', width: 'w-24', desc: true,
          hint: 'Of this coder\u2019s own authored codes that QA has ruled on, '
            + 'the share accepted. Reviewed count in brackets. BLANK below the '
            + 'minimum denominator — a rate off one or two codes is noise, and '
            + 'most coders sit below that floor.' },
        { key: 'practices_30d',    label: 'Practices', subLabel: '30d', width: 'w-24', desc: true,
          hint: 'Distinct practices worked in the last 30 days. A high count '
            + 'against few tasks means the coder is spread thin.' },
      ],
      // SUGGESTED RULES. Each row is a practice-tier where started work cannot
      // proceed because no active rule routes it. Deliberately does NOT name a
      // coder — the gap is a fact, the person who fills it is a staffing call.
      rulesColumns: [
        { key: 'practice_name', label: 'Practice', align: 'left', width: 'w-72', desc: false,
          hint: 'The practice holding blocked work. Enable a rule for it at the '
            + 'tier shown, on the Assignments tab — this view creates nothing.' },
        { key: 'tier', label: 'Tier', align: 'left', width: 'w-24', desc: false,
          hint: 'The review tier with no active assignment rule. Work has '
            + 'reached this tier and stopped here.' },
        { key: 'raf', label: 'RAF', subLabel: 'blocked', width: 'w-24', desc: true,
          hint: 'RAF already suggested on the blocked charts — what is at risk '
            + 'if they never move. THE RANKING IS ON THIS, not chart count: 50 '
            + 'low-value charts should not outrank 20 high-value ones.' },
        { key: 'charts', label: 'Charts', width: 'w-20', desc: true,
          hint: 'Charts a coder has already actioned that now sit at this tier '
            + 'with no rule to route them.' },
        { key: 'codes', label: 'Codes', width: 'w-20', desc: true,
          hint: 'Suggested codes sitting on those charts, awaiting review.' },
        { key: 'avg_age_days', label: 'Avg age', subLabel: 'days', width: 'w-24', desc: true,
          hint: 'Mean days the blocked tasks have been open. Age is not the '
            + 'ranking — RAF is — but a high average alongside modest RAF is '
            + 'still a practice being quietly starved.' },
        { key: 'oldest_days', label: 'Oldest', subLabel: 'days', width: 'w-24', desc: true,
          hint: 'The single longest-waiting blocked task at this practice-tier.' },
        { key: 'coders_at_tier', label: 'Coders', subLabel: 'at tier', width: 'w-24', desc: false,
          hint: 'Coders licensed for this tier ANYWHERE in the tenant. When this '
            + 'is 1 or 2, enabling a rule may not be the fix — work cannot be '
            + 'routed to capacity that does not exist. Confirm has one licensed '
            + 'coder tenant-wide; 3LR has three.' },
      ],
      practiceColumns: [
        // BOUNDED, not auto. Left to size itself the column stretched to fit a
        // 70-character outlier while the median name is 26 and p90 is 38, so a
        // handful of rows were setting the width for all 341. Truncated with
        // the full name on hover.
        { key: 'practice_name',    label: 'Practice',        align: 'left',  width: 'w-64', desc: false,
          hint: 'The practice the opportunity is attributed to. Badges beside '
            + 'the name flag an unrouted tier, a stalled 2LR queue, or closures '
            + 'lagging task throughput. Names are truncated — hover for the '
            + 'full name.' },
        // subLabel renders muted after the label, mirroring the cell's
        // primary / secondary weighting so the two numbers are unambiguous.
        { key: 'coders',           label: 'Coders', subLabel: 'now | ever',
          width: 'w-24', desc: true,
          hint: 'NOW = coders holding an active assignment rule at this '
            + 'practice. EVER = coders who have completed work here at any '
            + 'point. A red 0 means nobody is routed this practice even though '
            + 'people are working it.' },
        // The four under the "Last 30 days" band. Labels are single words
        // because the band already supplies the period and the noun — spelling
        // out "Tasks completed / Opps closed / Codes confirmed / RAF confirmed"
        // repeated a context the header directly above them states once, and
        // cost ~40pct of the table's width. Full wording lives in the hint.
        { key: 'recent',           label: 'Tasks',   width: 'w-20',  desc: true,
          hint: 'Review tasks a coder completed in the last 30 days, any '
            + 'tier. Excludes administrative bulk closures.' },
        { key: 'closed_30d',       label: 'Opps',    width: 'w-20',  desc: true,
          hint: 'Suspect opportunities that reached a terminal state in the '
            + 'last 30 days. One per chart, so this is charts finished — not '
            + 'tasks. Excludes administrative bulk closures.' },
        { key: 'confirmed_codes_30d', label: 'Codes', width: 'w-20', desc: true,
          hint: 'Suggested codes whose QA verdict at the HIGHEST tier they '
            + 'reached was an agreement, on opportunities closed in the last 30 '
            + 'days.' },
        { key: 'raf_30d',          label: 'RAF',     width: 'w-20',  desc: true,
          hint: 'CMS-HCC risk weight of those confirmed codes, model CNA '
            + '(Community Non-dual Aged). One model applied to every code, so '
            + 'it is a comparable weighting rather than a payment figure.' },
        { key: 'billed_pct',       label: 'Billed',  subLabel: '2mo', width: 'w-20', desc: true,
          hint: 'Share of QA-confirmed codes carried on a claim for the SAME '
            + 'HCC within 2 months of the opportunity closing. Matched on HCC '
            + 'not exact ICD — a coder suggests E1169, the PCP bills E1122, '
            + 'same risk captured. Codes closed less than 2 months ago are '
            + 'EXCLUDED, not counted as misses: their window is still open and '
            + 'their claims have not matured, so including them would measure '
            + 'lag rather than performance.' },
        // subLabel carries the qualifier so the label itself stays one word.
        { key: 'wip_2lr_raf',      label: 'RAF',     subLabel: 'in 2LR', width: 'w-24', desc: true,
          hint: 'RAF weight of confirmed-code candidates sitting in the 2LR '
            + 'queue right now. A STOCK, not a 30-day flow — this is value '
            + 'dammed up behind the stage that throttles the pipeline. Hover a '
            + 'cell for the chart and code counts.' },
        { key: 'not_started',      label: 'Unstarted', width: 'w-24', desc: true,
          hint: 'Open opportunities no coder has actioned at all — untapped '
            + 'backlog, as opposed to work in progress. A stock, not a 30-day '
            + 'flow. Hover a cell for its share of that practice\u2019s panel.' },
        { key: 'pct_per_year',     label: '%',       subLabel: 'panel/yr', width: 'w-20', desc: true,
          hint: 'Share of this practice\u2019s panel that would be reviewed in '
            + 'a year at its last-30-day closure rate. A projection, not an '
            + 'actual.' },
        { key: 'patients',         label: 'Panel',   width: 'w-20',  desc: true,
          hint: 'Patients currently attributed to this practice on an MSSP '
            + 'A- or D-prefixed contract for the performance year. The '
            + 'denominator for % panel/yr.' },
      ],
      // Column whose definition popup is open, and where to draw it. One at a
      // time. hintRect is the ⓘ icon's viewport rect, captured on hover, since
      // the teleported panel has no ancestor to position against.
      hintCol: null,
      hintRect: null,
      // Which basis the header is showing. 'projected' is the default: the
      // page's question is about pace, and YTD alone cannot answer it.
      scenario: 'projected',
      sortKey: 'pct_per_year',
      sortDesc: true,
      expanded: null,
      // Tiers that get an "unassigned" badge, in workflow order so the
      // earliest gap reads first. 1LR is DELIBERATELY EXCLUDED: 229 of 342
      // practices have no 1LR rule, so badging it would put the same mark on
      // 67pct of the table and bury the downstream gaps. The backend still
      // returns 1LR coverage if it is ever wanted.
      TIER_ORDER: ['2LR', '3LR', 'Confirm'],
      // Average age (days) of a practice's open 2LR tasks at which the queue
      // stops being normal WIP and becomes a holdup. Program distribution:
      // median 26d, p75 37d, p90 53d, max 96d. One threshold, not two — the
      // badge names the condition and the tooltip carries the day count, so
      // there is nothing for a second tier to express.
      WIP_AGE_WARN: 45,
    };
  },
  computed: {
    t() {
      return this.data.total;
    },
    sc() {
      return this.data.scenarios[this.scenario] || {};
    },
    practiceEntityIds() {
      return this.selectedPracticeIds;
    },
    selectedPracticeNames() {
      const idSet = new Set(this.selectedPracticeIds);
      return this.practiceOptions
        .filter(o => idSet.has(o.value))
        .map(o => ({ id: o.value, label: o.label }));
    },
    viewTabs() {
      return [
        { id: 'practice', label: 'By Practice' },
        { id: 'coder', label: 'By Coder' },
        { id: 'rules', label: 'Suggested Rules' },
      ];
    },
    columns() {
      if (this.view === 'coder') return this.coderColumns;
      if (this.view === 'rules') return this.rulesColumns;
      return this.practiceColumns;
    },
    rowsForView() {
      if (this.view === 'coder') return this.data.coders || [];
      if (this.view === 'rules') return (this.rulesData || {}).rules || [];
      return this.data.practices || [];
    },
    hintStyle() {
      const h = this.hintRect;
      const base = 'width: 17rem; background-color: #FFFFFF; color: #353537; '
        + 'border: 1px solid #E8E9E9; box-shadow: 0 4px 12px rgba(0,0,0,0.10);';
      if (!h) return base;
      const top = `top: ${h.r.bottom + 6}px;`;
      // 17rem = 272px at the default root size.
      const right = Math.max(8, window.innerWidth - h.r.right);
      const left = Math.max(8, Math.min(h.r.left, window.innerWidth - 280));
      return h.alignLeft
        ? `${base} ${top} left: ${left}px;`
        : `${base} ${top} right: ${right}px;`;
    },
    // Derivation of the 2LR throttle scenario, on hover. Reads every figure
    // from the payload so the text cannot drift from the arithmetic.
    unthrottledBasis() {
      const t = this.data.total;
      const u = this.data.scenarios.unthrottled || {};
      const n = x => Number(x || 0).toLocaleString();
      return 'SCENARIO, not an actual. 2LR is the throttle: 1LR resolves '
        + `${n(t.lr1_resolved_30d)} charts per ${this.data.rate_days} days and `
        + `hands ${n(t.lr1_to_2lr_30d)} of them to 2LR, which resolves only `
        + `${n(t.lr2_resolved_30d)}. 3LR and Confirm keep pace with their own `
        + 'inflow, so 2LR is the only stage holding the pipeline back. If 2LR '
        + 'cleared at 1LR\u2019s rate, closures would rise from '
        + `${n(t.closed_30d)} to ${n(t.unthrottled_closures_30d)} per `
        + `${this.data.rate_days} days (${u.multiple}\u00d7) — capped at what `
        + '1LR actually screens, since a chart can only close once. Code yield '
        + 'and RAF per code are held at their measured values, so the only '
        + 'thing changing is 2LR throughput.';
    },
    // What "beta" actually means here, so it is a statement rather than a
    // disclaimer: which parts are measured, which are modelled, and what is
    // still unsettled.
    betaCaveat() {
      return 'PROTOTYPE — not yet an agreed reporting surface. '
        + 'Superuser-only, and the definitions behind these figures are still '
        + 'being settled with CDI. Measured directly: opportunities closed, '
        + 'codes confirmed, RAF, per-tier throughput and queue age. Modelled: '
        + 'the annualised projections, and the "2LR at 1LR rate" scenario. '
        + 'Scope differs from Operations V2 (see the note beside this). '
        + 'Do not quote these numbers externally without checking the basis.';
    },
    scopeCaveat() {
      return 'This page EXCLUDES administrative bulk closures — opportunities '
        + 'closed by a mass action with no coder review — and counts only '
        + 'patients currently attributed to an MSSP A/D contract. Operations V2 '
        + 'excludes neither, so it reports the same measures materially higher. '
        + 'Neither is wrong: V2 answers "all recorded activity", this page '
        + 'answers "reviews we actually completed on the panel we hold today".';
    },
    trendChart() {
      const t = this.data.trend;
      return {
        labels: t.map(r => r.week),
        series: [
          // New opportunities are BATCH-created (tens of thousands in a single
          // week vs hundreds of tasks), so they get the right axis. On a shared
          // axis they flatten the two throughput lines to nothing.
          {
            values: t.map(r => r.opps_created),
            config: {
              label: 'New opportunities',
              backgroundColor: 'rgba(165, 165, 166, 0.35)',
              type: 'bar',
              yAxis: 'right',
              offset: 0,
            },
          },
          {
            values: t.map(r => r.tasks_actioned),
            config: {
              label: 'Tasks actioned',
              backgroundColor: '#21426C',
              type: 'line',
              yAxis: 'left',
              offset: 0,
            },
          },
          {
            values: t.map(r => r.opps_closed),
            config: {
              label: 'Opportunities closed',
              backgroundColor: '#F76734',
              type: 'line',
              yAxis: 'left',
              offset: 0,
            },
          },
          {
            values: t.map(r => r.codes_confirmed),
            config: {
              label: 'Codes confirmed',
              backgroundColor: '#389A6C',
              type: 'line',
              yAxis: 'left',
              offset: 0,
            },
          },
          // STOCK, not flow. Every other series is activity DURING the week;
          // this is RAF still waiting in 2LR AS AT the close of that week.
          // 2LR is the binding stage — every 1LR chart advances into it and it
          // is where an opportunity closes out — so this is the value dammed
          // up behind it. Left axis, per spec.
          {
            values: t.map(r => r.wip_2lr_raf),
            config: {
              label: 'RAF waiting in 2LR (as at)',
              backgroundColor: '#B45309',
              type: 'line',
              yAxis: 'left',
              offset: 0,
              // Dashed so it reads as a stock line, not another throughput
              // series. Chart_V5 expects strokeDasharray, not `dashed`.
              strokeDasharray: '6 4',
            },
          },
        ],
      };
    },
    trendConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 60, right: 80, bottom: 60, left: 70 },
        xAxis: { labelRotation: -45, fontSize: 11 },
        yAxes: [
          { position: 'left', min: 0, max: null, fontSize: 11 },
          { position: 'right', min: 0, max: null, fontSize: 11 },
        ],
        legend: { display: true, position: 'top', spacing: 15 },
        formatting: { percentage: false, currency: false, sig_figs: 0 },
        labelDisplayConfig: { minLabelSpacing: 1, labelFontSize: 11 },
      };
    },
    // COLUMN TOTALS for the practice view, frozen with the header.
    //
    // RATES ARE RECOMPUTED FROM SUMMED NUMERATORS AND DENOMINATORS, never
    // averaged across practices. A mean of per-practice percentages weights a
    // 40-patient practice the same as a 8,000-patient one, so it is not the
    // program's rate — it is the average of 325 unrelated rates. billed_pct and
    // pct_per_year therefore rebuild from their own components.
    //
    // Columns with no meaningful total render blank rather than 0: Coders would
    // double-count anyone working several practices, and Tier is categorical.
    // A blank says "this does not sum"; a 0 would assert something false.
    totals() {
      if (this.view !== 'practice') return null;
      const rows = this.sorted;
      if (!rows.length) return null;
      const sum = k => rows.reduce((n, r) => n + (r[k] || 0), 0);

      const patients = sum('patients');
      const billedDenom = sum('billed_denom');
      const billedAfter = sum('billed_after');
      // Annualised from the 30-day closure flow, matching the per-row column.
      const perYear = sum('closed_30d') / this.data.rate_days * 365;

      return {
        practice_name: `${rows.length} practices`,
        coders: null,
        recent: sum('recent'),
        closed_30d: sum('closed_30d'),
        confirmed_codes_30d: sum('confirmed_codes_30d'),
        raf_30d: sum('raf_30d'),
        billed_pct: billedDenom ? billedAfter / billedDenom : null,
        wip_2lr_raf: sum('wip_2lr_raf'),
        not_started: sum('not_started'),
        pct_per_year: patients ? perYear / patients : null,
        patients,
      };
    },
    sorted() {
      const rows = [...this.rowsForView];
      const dir = this.sortDesc ? -1 : 1;
      const coder = this.view === 'coder';
      return rows.sort((a, b) => {
        const x = this.sortValue(a);
        const y = this.sortValue(b);
        if (typeof x === 'string') return dir * x.localeCompare(y);
        // Tiebreak on the grain's own volume measure so equal values (the
        // 30-day columns carry a lot of zeros) land in a stable, sensible
        // order rather than whatever order the API happened to return.
        if (this.view === 'rules') return dir * (x - y) || b.raf - a.raf;
        return dir * (x - y)
          || (coder ? b.tasks_30d - a.tasks_30d : b.patients - a.patients);
      });
    },
  },
  methods: {
    // Position the definition popup from the icon's own rect. Right-aligned
    // columns anchor their RIGHT edge so a panel on the last column opens
    // inward rather than off-screen; the 8px floor stops it touching the edge.
    openHint(col, ev) {
      const r = ev.currentTarget.getBoundingClientRect();
      this.hintCol = col.key;
      this.hintRect = { r, alignLeft: col.align === 'left' };
    },
    // Click a header: first click takes that column's natural direction,
    // clicking the live column again reverses it.
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDesc = !this.sortDesc;
        return;
      }
      this.sortKey = key;
      this.sortDesc = this.columns.find(c => c.key === key)?.desc ?? true;
    },
    // Tier colours identify WHICH rule served the chart — category, not
    // severity, the same principle as the practice badges. Each carries a
    // matching border so they hold their edges against the white card and stay
    // distinguishable to a colour-blind reader by weight as well as by hue.
    // Rework is the exception in the flow, so it is the one that reads hot.
    hopperTierStyle(tier) {
      const map = {
        rework:
          'background-color: #FEF0C7; color: #93370D; border: 1px solid #FCD34D;',
        foundations_anchor:
          'background-color: #E0EAFF; color: #3538CD; border: 1px solid #A5B4FC;',
        foundations_global:
          'background-color: #E0EAFF; color: #3538CD; border: 1px solid #A5B4FC;',
        pvp_batch:
          'background-color: #F1F9F0; color: #485446; border: 1px solid #A8CCA3;',
        global:
          'background-color: #F0FDFA; color: #0F766E; border: 1px solid #5EEAD4;',
      };
      return map[tier]
        || 'background-color: #F4F4F4; color: #626264; border: 1px solid #DDDDDD;';
    },
    onPracticeChange() {
      clearTimeout(this._practiceDebounce);
      this._practiceDebounce = setTimeout(() => this.fetchPace(), 500);
    },
    removePractice(id) {
      this.selectedPracticeIds = this.selectedPracticeIds.filter(v => v !== id);
      this.onPracticeChange();
    },
    clearPractices() {
      this.selectedPracticeIds = [];
      this.fetchPace();
    },
    setYear(y) {
      if (this.year === y) return;
      this.year = y;
      this.fetchPace();
    },
    buildPracticeOptions(practices) {
      if (!practices) return;
      this.practiceOptions = practices
        .map(p => ({ value: p.practice_entity_id, label: p.practice_name }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    onViewTab(id) {
      this.setView(id);
    },
    async fetchRules() {
      if (this.rulesData || this.rulesLoading) return;
      this.rulesLoading = true;
      this.rulesError = null;
      try {
        const params = { program: 'Suspect', year: this.year };
        if (this.practiceEntityIds.length) {
          params.practice_entity_ids = this.practiceEntityIds.join(',');
        }
        const { data } = await axios.get('api/coding/suggested-rules/', {
          params,
        });
        this.rulesData = data;
      } catch (e) {
        this.rulesError = e.response?.data?.error
          || 'Could not work out which rules are blocking.';
      } finally {
        this.rulesLoading = false;
      }
    },
    async fetchHopper(coderId) {
      // Cached, in-flight, or previously errored — don't refetch on re-expand.
      if (this.hopper[coderId] || this.hopperLoading[coderId]) return;
      this.hopperLoading = { ...this.hopperLoading, [coderId]: true };
      this.hopperError = { ...this.hopperError, [coderId]: null };
      try {
        const { data } = await axios.get('api/coding/projected-hopper/', {
          params: { coder_entity_id: coderId, depth: 5 },
        });
        this.hopper = { ...this.hopper, [coderId]: data.projected || [] };
      } catch (e) {
        this.hopperError = {
          ...this.hopperError,
          [coderId]: e.response?.data?.error || 'Could not calculate the hopper.',
        };
      } finally {
        this.hopperLoading = { ...this.hopperLoading, [coderId]: false };
      }
    },
    toggleCoder(c) {
      this.expandedCoder = this.expandedCoder === c.coder_id ? null : c.coder_id;
      if (this.expandedCoder === c.coder_id) this.fetchHopper(c.coder_id);
    },
    // 1LR-only vs QA authority, from the coder's RULES not their activity.
    // A screener and a reviewer produce different events — a rejection from a
    // 2LR carries authority a 1LR's does not — so the two are separated
    // categorically rather than left as a comma-joined tier string.
    // 37 of 44 coders are screeners, which the tier list obscured.
    coderAuthority(c) {
      const higher = [];
      if (c.has_2lr) higher.push('2LR');
      if (c.has_3lr) higher.push('3LR');
      if (c.has_confirm) higher.push('Confirm');
      if (!higher.length) {
        return {
          label: c.rule_practices ? '1LR only' : 'no rules',
          style: c.rule_practices
            ? 'background-color: #F3F4F6; color: #626264;'
            : 'background-color: #FEF2F2; color: #DC2626;',
          title: c.rule_practices
            ? `Screener — 1LR assignment rules only, across `
              + `${c.rule_practices} practice(s).`
            : 'No practice-grain assignment rules. Work reaches this coder by a '
              + 'global or TIN-scoped rule, or by direct assignment.',
        };
      }
      return {
        label: higher.join(' / '),
        // Teal, matching the 3LR badge family on the practice view — QA
        // authority reads as a different KIND of thing, not a severity.
        style: 'background-color: #F0FDFA; color: #0F766E; border: 1px solid #5EEAD4;',
        title: `Holds QA authority: ${higher.join(', ')} assignment rules `
          + `across ${c.rule_practices} practice(s).`,
      };
    },
    // Switching grain must reset the sort: the two views share no sort keys, and
    // a stale key silently sorts by undefined — every row ties and the order
    // looks random.
    setView(view) {
      if (this.view === view) return;
      this.view = view;
      this.expanded = null;
      this.expandedCoder = null;
      this.hintCol = null;
      if (view === 'rules') this.sortKey = 'raf';
      else if (view === 'coder') this.sortKey = 'tasks_30d';
      else this.sortKey = 'pct_per_year';
      this.sortDesc = true;
      if (view === 'rules') this.fetchRules();
    },
    // Nulls sort as the least-favourable value in whichever direction is
    // active, so "no data" never floats to the top of a descending sort.
    sortValue(p) {
      const k = this.sortKey;
      if (k === 'practice_name') return p.practice_name || '';
      if (k === 'coder') return p.coder || '';
      // Sort authority by rank, not label text: screeners at one end, the
      // highest tier held at the other.
      if (k === 'authority') {
        return (p.has_confirm ? 4 : 0) + (p.has_3lr ? 2 : 0) + (p.has_2lr ? 1 : 0);
      }
      if (k === 'coders') return this.ruledCount(p);
      const v = p[k];
      // Nulls sort as the least-favourable value in whichever direction is
      // active, so suppressed QA rates never float to the top.
      if (v == null) return this.sortDesc ? -Infinity : Infinity;
      return v;
    },
    toggle(p) {
      this.expanded = this.expanded === p.practice_entity_id ? null : p.practice_entity_id;
    },
    ruledCount(p) {
      return (p.coders || []).filter(c => c.is_assigned).length;
    },
    // Splits the child panel into the two populations that matter:
    // who is assigned by rule, and who worked the practice without one.
    // Only non-empty groups are returned, so a practice whose coders all hold
    // rules shows no second heading at all.
    coderGroups(p) {
      const byTier = (a, b) => a.task_type_id - b.task_type_id
        || (b.tasks_30d || 0) - (a.tasks_30d || 0);
      const sum = rows => rows.reduce((n, c) => n + (c.tasks_30d || 0), 0);
      const assigned = p.coders.filter(c => c.is_assigned).sort(byTier);
      // No active rule at this practice. Ordered by work done, descending —
      // the one who did the most unruled work is the one to ask about.
      const unruled = p.coders.filter(c => !c.is_assigned)
        .sort((a, b) => (b.tasks_30d || 0) - (a.tasks_30d || 0));
      const groups = [];
      if (assigned.length) {
        groups.push({
          key: 'assigned',
          label: 'Currently assigned by rule',
          rows: assigned,
          tasks: sum(assigned),
        });
      }
      if (unruled.length) {
        groups.push({
          key: 'unruled',
          label: 'Worked without a rule',
          rows: unruled,
          tasks: sum(unruled),
        });
      }
      return groups;
    },
    num(v) {
      return v == null ? '—' : Math.round(v).toLocaleString();
    },
    pct(v) {
      return v == null ? '—' : `${(v * 100).toFixed(1)}%`;
    },
    raf(v) {
      return v == null ? '—' : Number(v).toLocaleString(undefined, {
        minimumFractionDigits: 1, maximumFractionDigits: 1,
      });
    },
    // ALL practice-level flags, rendered as badges beside the practice name.
    // ONE COLOUR PER FLAG so each is recognisable without reading it.
    // Ordered CAUSE BEFORE SYMPTOM — an unrouted queue explains a stalled one.
    //   'Unassigned: <tiers>'  red    — ONE badge listing every tier that holds
    //                          open charts with no active rule. Combined rather
    //                          than one badge per tier: they are the same
    //                          problem at different stages, and several
    //                          separate badges on one row read as several
    //                          unrelated faults.
    //   '2LR stalled'          amber  — 17 practices
    //   'Low closure rate'     indigo — closures below what throughput implies
    //   'Nothing closing'      indigo — same flag, absolute form, same hue
    // Measured 2026-07-28 (unrouted charts / practices with work but no rule at
    // that tier): 2LR 6,070 / 60 of 115 · 3LR 134 / 20 of 26 · Confirm 7 / 5 of 6.
    practiceFlags(p) {
      const out = [];
      const badge = (bg, fg, bd) => `background-color: ${bg}; color: ${fg}; `
        + `border: 1px solid ${bd};`;
      const RED = badge('#FEF2F2', '#DC2626', '#FCA5A5');
      const AMBER = badge('#FFFBEB', '#B45309', '#FCD34D');
      const INDIGO = badge('#EEF2FF', '#4338CA', '#A5B4FC');
      const opps = p.wip_2lr_opps || 0;
      const raf = p.wip_2lr_raf || 0;
      const age = p.wip_2lr_age_days || 0;

      // Every tier holding open charts with NO active rule at that tier,
      // collapsed into ONE badge whose label names the tiers. Tier-by-tier
      // because a rule at one tier does not route another tier's work.
      // 1LR is excluded — see TIER_ORDER.
      const cov = p.tier_coverage || {};
      const gaps = this.TIER_ORDER.filter((tier) => {
        const t = cov[tier];
        return t && t.charts && !t.has_rule;
      });
      if (gaps.length) {
        const charts = gaps.reduce((n, tier) => n + cov[tier].charts, 0);
        out.push({
          key: 'unassigned',
          label: `Unassigned: ${gaps.join(', ')}`,
          style: RED,
          title: 'No active assignment rule at this practice for '
            + `${gaps.join(', ')}. ${charts.toLocaleString()} charts waiting `
            + `(${gaps.map(t => `${t} ${cov[t].charts.toLocaleString()}`)
              .join(', ')}). Nobody is routed this work.`,
        });
      }

      if (opps && age >= this.WIP_AGE_WARN) {
        out.push({
          key: 'age',
          label: '2LR stalled',
          style: AMBER,
          title: `Open 2LR tasks here are ${age} days old on average `
            + `(${opps.toLocaleString()} charts, ${raf.toFixed(1)} RAF). `
            + 'Program median is 26 days.',
        });
      }

      const ratio = p.close_ratio;
      const worked = p.recent || 0;
      if (ratio != null && worked && ratio < 0.6) {
        const stalled = ratio === 0;
        out.push({
          key: 'close',
          label: stalled ? 'Nothing closing' : 'Low closure rate',
          style: INDIGO,
          // The ratio itself lives in the tooltip: on the badge it read as a
          // number needing decoding, and the label already says what it means.
          title: stalled
            ? `${worked.toLocaleString()} tasks completed here, no `
              + 'opportunities closed.'
            : `Closing at ${ratio.toFixed(2)}x the rate this practice's `
              + `${worked.toLocaleString()} completed tasks should have `
              + 'produced.',
        });
      }
      return out;
    },

    years(v) {
      if (v === 0) return 'complete';
      if (v == null) return 'no activity';
      return v >= 100 ? '100+' : v;
    },
    yearsColor(v) {
      if (v === 0) return '#389A6C';
      if (v == null) return '#DC2626';
      if (v > 10) return '#DC2626';
      if (v > 3) return '#D97706';
      return '#389A6C';
    },
    async fetchPace() {
      this.loading = true;
      this.error = null;
      this.expanded = null;
      this.expandedCoder = null;
      // The hopper is live Postgres state, not part of this payload — but it
      // must not outlive a reload, or an expanded panel would show a sequence
      // calculated against data the rest of the page has since replaced.
      this.hopper = {};
      this.hopperLoading = {};
      this.hopperError = {};
      // Same reasoning as the hopper: this is live state, not part of the pace
      // payload, and must not outlive a year change.
      this.rulesData = null;
      this.rulesError = null;
      if (this.view === 'rules') this.fetchRules();
      try {
        const params = { program: 'Suspect', year: this.year };
        if (this.practiceEntityIds.length) {
          params.practice_entity_ids = this.practiceEntityIds.join(',');
        }
        const { data } = await axios.get('api/coding/pace/', { params });
        this.data = data;
        if (!this.practiceEntityIds.length) {
          this.buildPracticeOptions(data.practices);
        }
      } catch (e) {
        this.error = e?.response?.data?.error || 'Could not load estimate.';
        this.data = null;
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.fetchPace();
  },
};
</script>
